import React, { useState } from 'react';
import { Upload, Button, Table, message, Modal, Card, Alert, Tag, Space, Collapse, Tooltip } from 'antd';
import { UploadOutlined, DownloadOutlined, CheckCircleOutlined, CloseCircleOutlined, FileExcelOutlined, InfoCircleOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import { computeImmatriculation } from './data/donnees';

const { Panel } = Collapse;

// En-têtes canoniques attendus par la validation.
const CANONICAL_HEADERS = [
    'Type Série', 'Matricule', 'Numéro Série', 'Marque', 'Énergie', 'Usage',
    'Volume/Puissance', 'Nombre Places', 'Genre', 'N° Moteur', 'Type', 'N° Châssis',
    'Carrosserie', 'Valeur Vénale', 'Valeur Déclarée', 'Valeur à Neuf',
    'Année Mise en Circulation', 'Garanties Supplémentaires', 'Conducteur Différent',
    'Conducteur - Nom', 'Conducteur - Prénom', 'Conducteur - Téléphone',
    'Conducteur - Adresse', 'Conducteur - Profession',
];

// Normalise un libellé d'en-tête : accents composés/décomposés (NFKC), espaces (y compris
// insécables) et casse → permet de retrouver la colonne même si l'en-tête diffère légèrement.
const normHeader = (s) => String(s == null ? '' : s)
    .normalize('NFKC')
    .replace(/ /g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const CANONICAL_BY_NORM = CANONICAL_HEADERS.reduce((acc, h) => { acc[normHeader(h)] = h; return acc; }, {});

// Remappe les clés d'une ligne vers les en-têtes canoniques (tolérant aux variations d'en-tête).
const canonicalizeRow = (row) => {
    const out = {};
    Object.keys(row || {}).forEach((k) => {
        const canon = CANONICAL_BY_NORM[normHeader(k)];
        out[canon || k] = row[k];
    });
    return out;
};

const FlotteExcelImport = ({ visible, onClose, onImportSuccess, marques, carosseries, categorie, zIndex, valeurParDefaut }) => {
    const [fileData, setFileData] = useState([]);
    const [validEngins, setValidEngins] = useState([]);
    const [errors, setErrors] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [importing, setImporting] = useState(false);

    // Télécharger le modèle Excel pour flotte
    // Valeur par défaut (vénale / déclarée / à neuf) — fournie par l'appelant (ex.
    // incorporation moto = 250000). Sert de pré-remplissage du modèle ET de repli à l'import.
    const vDef = (valeurParDefaut != null && !isNaN(Number(valeurParDefaut))) ? String(valeurParDefaut) : null;
    const vDefNum = vDef != null ? Number(vDef) : null;

    const downloadFleetTemplate = () => {
        const template = [
            {
                'Type Série': 'TG',
                'Matricule': '1234',
                'Numéro Série': 'AB',
                'Marque': 'TOYOTA',
                'Énergie': 'DIESEL',
                'Usage': 'PRIVE',
                'Volume/Puissance': '2500',
                'Nombre Places': '5',
                'Genre': '',
                'N° Moteur': 'MOT123456',
                'Type': 'BERLINE',
                'N° Châssis': 'CHAS123456789',
                'Carrosserie': 'STANDARD',
                'Valeur Vénale': vDef || '5000000',
                'Valeur Déclarée': vDef || '5000000',
                'Valeur à Neuf': vDef || '8000000',
                'Année Mise en Circulation': '2020',
                'Garanties Supplémentaires': 'VOL;INCENDIE',
                'Conducteur Différent': 'NON',
                'Conducteur - Nom': '',
                'Conducteur - Prénom': '',
                'Conducteur - Téléphone': '',
                'Conducteur - Adresse': '',
                'Conducteur - Profession': ''
            },
            {
                'Type Série': 'TG',
                'Matricule': '5678',
                'Numéro Série': 'CD',
                'Marque': 'HONDA',
                'Énergie': 'ESSENCE',
                'Usage': 'PRIVE',
                'Volume/Puissance': '1800',
                'Nombre Places': '4',
                'Genre': 'MOTOCYCLETTE',
                'N° Moteur': 'MOT789012',
                'Type': 'BERLINE',
                'N° Châssis': 'CHAS987654321',
                'Carrosserie': 'STANDARD',
                'Valeur Vénale': vDef || '3500000',
                'Valeur Déclarée': vDef || '3500000',
                'Valeur à Neuf': vDef || '6000000',
                'Année Mise en Circulation': '2019',
                'Garanties Supplémentaires': '',
                'Conducteur Différent': 'OUI',
                'Conducteur - Nom': 'ABLAVI',
                'Conducteur - Prénom': 'Marie',
                'Conducteur - Téléphone': '91234567',
                'Conducteur - Adresse': 'Lomé, Agoè',
                'Conducteur - Profession': 'CHAUFFEUR'
            }
        ];

        const ws = XLSX.utils.json_to_sheet(template);

        // Ajuster la largeur des colonnes
        const colWidths = [
            { wch: 12 }, { wch: 12 }, { wch: 15 }, { wch: 15 }, { wch: 12 },
            { wch: 10 }, { wch: 18 }, { wch: 15 }, { wch: 15 }, { wch: 15 },
            { wch: 12 }, { wch: 18 }, { wch: 15 }, { wch: 15 }, { wch: 15 },
            { wch: 15 }, { wch: 25 }, { wch: 30 }, { wch: 20 }, { wch: 20 },
            { wch: 20 }, { wch: 25 }, { wch: 25 }, { wch: 25 }
        ];
        ws['!cols'] = colWidths;

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Flotte Engins');

        XLSX.writeFile(wb, `modele_flotte_${categorie || 'AUTO'}.xlsx`);
        message.success('Modèle de flotte téléchargé avec succès!');
    };

    // Parser une date
    const parseDate = (dateValue) => {
        if (!dateValue) return null;

        if (typeof dateValue === 'number') {
            // Un entier à 4 chiffres dans une plage d'année = ANNÉE (ex. 2019), PAS un numéro de
            // série Excel (sinon 2019 → ~1905). On le traite comme le 1er janvier de cette année.
            if (Number.isInteger(dateValue) && dateValue >= 1900 && dateValue <= 2100) {
                return dayjs(`${dateValue}-01-01`);
            }
            const date = XLSX.SSF.parse_date_code(dateValue);
            if (!date) return null;
            return dayjs(`${date.y}-${String(date.m).padStart(2, '0')}-${String(date.d).padStart(2, '0')}`);
        }

        if (typeof dateValue === 'string') {
            // Format JJ/MM/AAAA
            const parts = dateValue.split('/');
            if (parts.length === 3) {
                return dayjs(`${parts[2]}-${parts[1]}-${parts[0]}`);
            }
            // Format AAAA
            if (parts.length === 1 && parts[0].length === 4) {
                return dayjs(`${parts[0]}-01-01`);
            }
        }

        return null;
    };

    // Valider et transformer les engins
    const validateAndTransformEngins = (rawData) => {
        const valid = [];
        const invalid = [];

        rawData.forEach((row, index) => {
            const rowErrors = [];
            const lineNumber = index + 2;

            try {
                // Type Série : lecture TOLÉRANTE (espaces, casse, caractères invisibles) — un
                // « TG » saisi avec un espace ou en minuscules ne doit pas faire échouer la ligne.
                const typeSerieUpper = String(row['Type Série'] ?? '').toUpperCase().replace(/[^A-Z]/g, '');
                const typeSerieCanon = typeSerieUpper === 'TG' ? 'TG' : 'Autre';

                // Validations obligatoires
                if (!typeSerieUpper || !['TG', 'AUTRE'].includes(typeSerieUpper)) {
                    rowErrors.push('Type Série invalide (TG ou Autre)');
                }
                if (!row['Matricule']) {
                    rowErrors.push('Matricule requis');
                }
                if (typeSerieUpper === 'TG' && !row['Numéro Série']) {
                    rowErrors.push('Numéro Série requis pour type TG');
                }
                if (!row['Marque']) {
                    rowErrors.push('Marque requise');
                }
                if (!row['Énergie'] || !['ESSENCE', 'DIESEL', 'ELECTRIQUE'].includes(row['Énergie'])) {
                    rowErrors.push('Énergie invalide (ESSENCE, DIESEL ou ELECTRIQUE)');
                }
                if (!row['Usage'] || !['PRIVE', 'TAXI'].includes(row['Usage'])) {
                    rowErrors.push('Usage invalide (PRIVE ou TAXI)');
                }
                if (!row['Volume/Puissance'] || isNaN(Number(row['Volume/Puissance']))) {
                    rowErrors.push('Volume/Puissance invalide');
                }
                if (!row['Nombre Places'] || isNaN(Number(row['Nombre Places']))) {
                    rowErrors.push('Nombre Places invalide');
                }
                if (!row['N° Moteur']) {
                    rowErrors.push('N° Moteur requis');
                }
                if (!row['Type']) {
                    rowErrors.push('Type requis');
                }
                if (!row['N° Châssis']) {
                    rowErrors.push('N° Châssis requis');
                }
                // Valeurs : si un défaut est fourni (ex. incorporation moto = 250000), une
                // cellule vide est tolérée (remplacée par le défaut) ; une valeur présente mais
                // non numérique reste une erreur.
                const valeurManquante = (v) => v === undefined || v === null || v === '';
                const valeurNonNumerique = (v) => !valeurManquante(v) && isNaN(Number(v));
                if (valeurManquante(row['Valeur Vénale']) ? vDefNum == null : valeurNonNumerique(row['Valeur Vénale'])) {
                    rowErrors.push('Valeur Vénale invalide');
                }
                if (valeurManquante(row['Valeur Déclarée']) ? vDefNum == null : valeurNonNumerique(row['Valeur Déclarée'])) {
                    rowErrors.push('Valeur Déclarée invalide');
                }
                if (valeurManquante(row['Valeur à Neuf']) ? vDefNum == null : valeurNonNumerique(row['Valeur à Neuf'])) {
                    rowErrors.push('Valeur à Neuf invalide');
                }
                if (!row['Année Mise en Circulation']) {
                    rowErrors.push('Année de mise en circulation requise');
                }

                // Validation conducteur si différent
                if (row['Conducteur Différent'] === 'OUI') {
                    if (!row['Conducteur - Nom']) {
                        rowErrors.push('Nom du conducteur requis');
                    }
                    if (!row['Conducteur - Téléphone'] || !/^(22|90|91|92|93|96|98|99|70|71|79)\d{6}$/.test(row['Conducteur - Téléphone'])) {
                        rowErrors.push('Téléphone du conducteur invalide');
                    }
                }

                if (rowErrors.length > 0) {
                    invalid.push({
                        line: lineNumber,
                        data: row,
                        errors: rowErrors
                    });
                } else {

                    // Fonction helper pour convertir en string et uppercase
                    const toUpperSafe = (value) => {
                        if (!value) return '';
                        return String(value).toUpperCase();
                    };

                    // Construire l'immatriculation via la MÊME logique que la saisie manuelle
                    // (computeImmatriculation) → format cohérent. Pour une moto (TG) :
                    // numéro de série + type de série + matricule.
                    const immatriculation = computeImmatriculation(
                        categorie,
                        typeSerieCanon,
                        toUpperSafe(row['Numéro Série']),
                        toUpperSafe(row['Matricule'])
                    );

                    // Transformer les données
                    const transformed = {
                        typeSerie: typeSerieCanon,
                        matricule: toUpperSafe(row['Matricule']),
                        numeroSerie: toUpperSafe(row['Numéro Série']),
                        immatriculation: immatriculation,
                        marque: String(row['Marque'] || ''),
                        energie: row['Énergie'],
                        usage: row['Usage'],
                        volume: Number(row['Volume/Puissance']) || 0,
                        puissance: Number(row['Volume/Puissance']) || 0,
                        nombrePlace: Number(row['Nombre Places']) || 0,
                        genre: String(row['Genre'] || ''),
                        moteur: toUpperSafe(row['N° Moteur']),
                        type: toUpperSafe(row['Type']),
                        numeroChassis: toUpperSafe(row['N° Châssis']),
                        carosserie: String(row['Carrosserie'] || ''),
                        valeurVenale: Number(row['Valeur Vénale']) || (vDefNum != null ? vDefNum : 0),
                        valeurDeclaree: Number(row['Valeur Déclarée']) || (vDefNum != null ? vDefNum : 0),
                        valeurANeuf: Number(row['Valeur à Neuf']) || (vDefNum != null ? vDefNum : 0),
                        dateMiseEnCirculation: parseDate(row['Année Mise en Circulation']),
                        garantiesSupplementaires: row['Garanties Supplémentaires']
                            ? String(row['Garanties Supplémentaires']).split(';').filter(Boolean)
                            : [],
                        useSouscripteur: row['Conducteur Différent'] !== 'OUI',
                        personne: row['Conducteur Différent'] === 'OUI' ? {
                            nom: String(row['Conducteur - Nom'] || ''),
                            prenom: String(row['Conducteur - Prénom'] || ''),
                            telephone: String(row['Conducteur - Téléphone'] || ''),
                            adresse: String(row['Conducteur - Adresse'] || ''),
                            profession: String(row['Conducteur - Profession'] || ''),
                            contact: row['Conducteur - Téléphone'] ? [String(row['Conducteur - Téléphone'])] : []
                        } : {},
                        lineNumber
                    };

                    valid.push(transformed);
                }
            } catch (error) {
                invalid.push({
                    line: lineNumber,
                    data: row,
                    errors: [`Erreur de traitement: ${error.message}`]
                });
            }
        });

        return { valid, invalid };
    };

    // Gérer l'upload
    const handleFileUpload = (file) => {
        setUploading(true);
        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const rawJson = XLSX.utils.sheet_to_json(worksheet);

                if (rawJson.length === 0) {
                    message.error('Le fichier est vide');
                    setUploading(false);
                    return;
                }

                // Remappe les en-têtes vers leurs libellés canoniques (tolérant aux accents/espaces/casse).
                const jsonData = rawJson.map(canonicalizeRow);
                setFileData(jsonData);
                const { valid, invalid } = validateAndTransformEngins(jsonData);
                setValidEngins(valid);
                setErrors(invalid);

                if (invalid.length === 0) {
                    message.success(`${valid.length} engin(s) validé(s) avec succès`);
                } else {
                    message.warning(`${valid.length} valide(s), ${invalid.length} erreur(s)`);
                }
            } catch (error) {
                message.error('Erreur lors de la lecture du fichier');
                console.error(error);
            } finally {
                setUploading(false);
            }
        };

        reader.readAsArrayBuffer(file);
        return false;
    };

    // Supprimer un engin de la liste
    const handleRemoveEngin = (lineNumber) => {
        setValidEngins(prev => prev.filter(e => e.lineNumber !== lineNumber));
        message.success('Engin retiré de la liste');
    };

    // Colonnes du tableau d'erreurs
    const errorColumns = [
        {
            title: 'Ligne',
            dataIndex: 'line',
            key: 'line',
            width: 80,
            render: (text) => <Tag color="red">Ligne {text}</Tag>
        },
        {
            title: 'Immatriculation',
            key: 'immat',
            render: (_, record) => {
                const typeSerie = record.data['Type Série'];
                const matricule = record.data['Matricule'];
                const numeroSerie = record.data['Numéro Série'];
                return typeSerie === 'TG'
                    ? `${typeSerie} ${matricule} ${numeroSerie}`
                    : matricule;
            }
        },
        {
            title: 'Erreurs',
            dataIndex: 'errors',
            key: 'errors',
            render: (errors) => (
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {errors.map((err, idx) => (
                        <li key={idx} style={{ color: '#ff4d4f' }}>{err}</li>
                    ))}
                </ul>
            )
        }
    ];

    // Colonnes du tableau d'engins valides
    const validColumns = [
        {
            title: 'Ligne',
            dataIndex: 'lineNumber',
            key: 'lineNumber',
            width: 80,
            render: (text) => <Tag color="green">#{text}</Tag>
        },
        {
            title: 'Immatriculation',
            dataIndex: 'immatriculation',
            key: 'immatriculation',
            render: (text) => <strong>{text}</strong>
        },
        {
            title: 'Marque',
            dataIndex: 'marque',
            key: 'marque'
        },
        {
            title: 'Énergie',
            dataIndex: 'energie',
            key: 'energie',
            render: (text) => <Tag color="blue">{text}</Tag>
        },
        {
            title: 'Places',
            dataIndex: 'nombrePlace',
            key: 'nombrePlace',
            width: 80
        },
        {
            title: 'Châssis',
            dataIndex: 'numeroChassis',
            key: 'numeroChassis',
            render: (text) => <Tooltip title={text}><span>{text.substring(0, 10)}...</span></Tooltip>
        },
        {
            title: 'Valeur Vénale',
            dataIndex: 'valeurVenale',
            key: 'valeurVenale',
            render: (text) => `${text.toLocaleString()} F`
        },
        {
            title: 'Conducteur',
            key: 'conducteur',
            render: (_, record) => {
                if (record.useSouscripteur) {
                    return <Tag color="default">Souscripteur</Tag>;
                }
                return (
                    <Tooltip title={`${record.personne.nom} ${record.personne.prenom} - ${record.personne.telephone}`}>
                        <Tag color="orange">Spécifique</Tag>
                    </Tooltip>
                );
            }
        },
        {
            title: 'Action',
            key: 'action',
            width: 80,
            render: (_, record) => (
                <Button
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemoveEngin(record.lineNumber)}
                />
            )
        }
    ];

    // Importer les engins. onImportSuccess peut être ASYNCHRONE (ex. résolution des
    // sous-garanties côté affaire nouvelle) → on attend pour garder le loader actif.
    const handleImport = async () => {
        if (validEngins.length === 0) {
            message.error('Aucun engin valide à importer');
            return;
        }
        if (importing) return;
        setImporting(true);
        try {
            await onImportSuccess(validEngins);
            handleClose();
        } finally {
            setImporting(false);
        }
    };

    const handleClose = () => {
        setFileData([]);
        setValidEngins([]);
        setErrors([]);
        onClose();
    };

    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FileExcelOutlined style={{ fontSize: 24, color: '#52c41a' }} />
                    <span>Import de flotte d'engins depuis Excel</span>
                </div>
            }
            open={visible}
            onCancel={handleClose}
            width={1400}
            footer={null}
            zIndex={zIndex}
        >
            <Space direction="vertical" style={{ width: '100%' }} size="large">
                <Alert
                    message="Import de flotte pour la catégorie MOTO"
                    description="Vous pouvez importer plusieurs engins en une seule fois. Chaque ligne du fichier Excel représente un engin."
                    type="info"
                    showIcon
                    icon={<InfoCircleOutlined />}
                />

                <Card>
                    <Space direction="vertical" style={{ width: '100%' }} size="middle">
                        <Button
                            icon={<DownloadOutlined />}
                            onClick={downloadFleetTemplate}
                            type="primary"
                            size="large"
                            block
                        >
                            Télécharger le modèle Excel pour flotte
                        </Button>

                        <Upload
                            accept=".xlsx,.xls"
                            beforeUpload={handleFileUpload}
                            showUploadList={false}
                            disabled={uploading}
                        >
                            <Button
                                icon={<UploadOutlined />}
                                size="large"
                                block
                                loading={uploading}
                            >
                                {uploading ? 'Chargement...' : 'Charger un fichier Excel'}
                            </Button>
                        </Upload>
                    </Space>
                </Card>

                {(validEngins.length > 0 || errors.length > 0) && (
                    <Card>
                        <Space size="large" style={{ marginBottom: 16 }}>
                            <div>
                                <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 24 }} />
                                <span style={{ marginLeft: 8, fontSize: 18, fontWeight: 'bold' }}>
                                    {validEngins.length} engin(s) valide(s)
                                </span>
                            </div>
                            {errors.length > 0 && (
                                <div>
                                    <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 24 }} />
                                    <span style={{ marginLeft: 8, fontSize: 18, fontWeight: 'bold' }}>
                                        {errors.length} erreur(s)
                                    </span>
                                </div>
                            )}
                        </Space>

                        {errors.length > 0 && (
                            <Collapse style={{ marginBottom: 16 }}>
                                <Panel
                                    header={
                                        <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
                                            <CloseCircleOutlined /> Erreurs détectées ({errors.length})
                                        </span>
                                    }
                                    key="errors"
                                >
                                    <Alert
                                        message="Certaines lignes contiennent des erreurs"
                                        description="Corrigez les erreurs dans votre fichier Excel et réimportez-le. Les engins valides peuvent être importés."
                                        type="error"
                                        showIcon
                                        style={{ marginBottom: 16 }}
                                    />
                                    <Table
                                        columns={errorColumns}
                                        dataSource={errors}
                                        pagination={{ pageSize: 5 }}
                                        size="small"
                                        rowKey="line"
                                    />
                                </Panel>
                            </Collapse>
                        )}

                        {validEngins.length > 0 && (
                            <>
                                <h3 style={{ marginBottom: 16 }}>
                                    <CheckCircleOutlined style={{ color: '#52c41a' }} /> Engins prêts à importer
                                </h3>
                                <Table
                                    columns={validColumns}
                                    dataSource={validEngins}
                                    pagination={{ pageSize: 10 }}
                                    size="small"
                                    rowKey="lineNumber"
                                    scroll={{ x: 1200 }}
                                />
                            </>
                        )}
                    </Card>
                )}

                {validEngins.length > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        <Button onClick={handleClose} size="large" disabled={importing}>
                            Annuler
                        </Button>
                        <Button
                            type="primary"
                            onClick={handleImport}
                            icon={<CheckCircleOutlined />}
                            size="large"
                            loading={importing}
                        >
                            {importing ? 'Importation…' : `Importer ${validEngins.length} engin(s)`}
                        </Button>
                    </div>
                )}
            </Space>
        </Modal>
    );
};

export default FlotteExcelImport;